import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Alert, Avatar, Button, DatePicker, Space, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { listMonthlyReports } from './monthlyReportsService';
import { MonthlyReportSummary } from '../types';

dayjs.extend(utc);

const defaultMonth = (): string => dayjs.utc().subtract(1, 'day').format('YYYY-MM');

const MonthlyReportsDashboard: React.FC = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const month = useMemo(() => {
        const raw = searchParams.get('month');
        return raw && /^\d{4}-(0[1-9]|1[0-2])$/.test(raw) ? raw : defaultMonth();
    }, [searchParams]);

    const [rows, setRows] = useState<MonthlyReportSummary[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await listMonthlyReports(month);
            setRows(data);
        } catch (err: any) {
            setError(err?.response?.data?.message || err?.message || 'Unknown error');
        } finally {
            setLoading(false);
        }
    }, [month]);

    useEffect(() => {
        load();
    }, [load]);

    useEffect(() => {
        document.title = 'Monthly Reports · Sonat Support';
    }, []);

    const handleMonthChange = (next: Dayjs | null) => {
        if (!next) return;
        setSearchParams({ month: next.format('YYYY-MM') });
    };

    const columns: ColumnsType<MonthlyReportSummary> = [
        {
            title: 'Member',
            key: 'login',
            render: (_, row) => (
                <Space>
                    <Avatar src={row.avatarUrl} size="small" />
                    <span>{row.login}</span>
                </Space>
            ),
        },
        {
            title: 'Status',
            key: 'status',
            render: (_, row) =>
                row.hasReport ? (
                    <Tag color="green">
                        Saved {row.lastEditedAt ? dayjs(row.lastEditedAt).format('YYYY-MM-DD') : ''}
                    </Tag>
                ) : (
                    <Tag>No report</Tag>
                ),
        },
        {
            title: '',
            key: 'action',
            width: 160,
            render: (_, row) => (
                <Link to={`/team-insights/reports/${encodeURIComponent(row.login)}?month=${month}`}>
                    <Button type={row.hasReport ? 'default' : 'primary'}>
                        {row.hasReport ? 'Open' : 'Generate'}
                    </Button>
                </Link>
            ),
        },
    ];

    return (
        <div style={{ padding: 16 }}>
            <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }}>
                <Typography.Title level={3} style={{ margin: 0 }}>
                    Monthly reports
                </Typography.Title>
                <DatePicker
                    picker="month"
                    value={dayjs(month + '-01')}
                    onChange={handleMonthChange}
                    allowClear={false}
                />
            </Space>

            {error && <Alert type="error" showIcon style={{ marginBottom: 16 }} message={error} />}

            <Table
                rowKey="login"
                loading={loading}
                dataSource={rows}
                columns={columns}
                pagination={false}
            />
        </div>
    );
};

export default MonthlyReportsDashboard;
